import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { CountryProvider } from './contexts/CountryContext';
import { AuthProvider, useAppAuth } from './contexts/AuthContext';

// Layouts
import { DashboardLayout } from './layouts/DashboardLayout';
import { AuthLayout } from './layouts/AuthLayout';

// Pages
import { LoginPage } from './pages/auth/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { VendorsPage } from './pages/vendors/VendorsPage';
import { VendorDetailPage } from './pages/vendors/VendorDetailPage';
import { StoresPage } from './pages/stores/StoresPage';
import { AssignmentsPage } from './pages/assignments/AssignmentsPage';
import { CollectionsPage } from './pages/collections/CollectionsPage';
import { OffersPage } from './pages/offers/OffersPage';
import { CompliancePage } from './pages/compliance/CompliancePage';
import { ExceptionsPage } from './pages/exceptions/ExceptionsPage';
import { DocumentsPage } from './pages/documents/DocumentsPage';
import { ServiceRequestsPage } from './pages/fexa/ServiceRequestsPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { AdminPage } from './pages/admin/AdminPage';
import { VendorPortalPage } from './pages/vendor-portal/VendorPortalPage';

import './App.css';

const clerkPubKey =
  process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ||
  process.env.VITE_CLERK_PUBLISHABLE_KEY;

function ProtectedRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  const { loading, user } = useAppAuth();

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8">
          <p className="text-muted-foreground">Loading secure session...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 max-w-md">
          <h1 className="text-2xl font-bold text-foreground mb-4">Access unavailable</h1>
          <p className="text-muted-foreground">
            Your session is active, but the application could not load your internal user profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Outlet />
  );
}

function App() {
  if (!clerkPubKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v18M3 12h18" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">Mind Your Waste OPS</h1>
          <p className="text-muted-foreground mb-6">
            Please configure REACT_APP_CLERK_PUBLISHABLE_KEY or VITE_CLERK_PUBLISHABLE_KEY in your environment variables to enable authentication.
          </p>
          <div className="p-4 bg-muted rounded-lg text-left">
            <p className="text-sm font-mono text-muted-foreground">
              REACT_APP_CLERK_PUBLISHABLE_KEY=pk_test_...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <BrowserRouter>
        <AuthProvider>
          <CountryProvider>
            <Routes>
              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/sign-in/*" element={<LoginPage />} />
                <Route path="/sign-up/*" element={<LoginPage />} />
              </Route>

              {/* Protected Dashboard Routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/vendors" element={<VendorsPage />} />
                  <Route path="/vendors/:id" element={<VendorDetailPage />} />
                  <Route path="/stores" element={<StoresPage />} />
                  <Route path="/assignments" element={<AssignmentsPage />} />
                  <Route path="/collections" element={<CollectionsPage />} />
                  <Route path="/offers" element={<OffersPage />} />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route path="/exceptions" element={<ExceptionsPage />} />
                  <Route path="/documents" element={<DocumentsPage />} />
                  <Route path="/service-requests" element={<ServiceRequestsPage />} />
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                </Route>
                
                {/* Vendor Portal (separate layout) */}
                <Route path="/vendor-portal/*" element={<VendorPortalPage />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </CountryProvider>
        </AuthProvider>
      </BrowserRouter>
    </ClerkProvider>
  );
}

export default App;
